import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { DragDropTable, TableColumn, SortConfig } from '../../components/ui/drag-drop-table';
import { EnhancedPagination } from '../../components/ui/enhanced-pagination';
import { BulkActionsToolbar, BulkAction } from '../../components/ui/bulk-actions-toolbar';
import { useBulkSelection } from '../../hooks/use-bulk-selection';
import { useUIStore } from '../../stores/ui-store';
import { ExerciseForm } from './ExerciseForm';
import { InstructorPreviewModal } from './InstructorPreviewModal';
import { ApiErrorFallback } from '../../components/common/ApiErrorFallback';
import { LoadingState } from '../../components/common/LoadingState';
import { EmptyState } from '../../components/common/EmptyState';
import {
  usePlatformExerciseList,
  useDeletePlatformExercise,
  useReactivatePlatformExercise,
  useReorderPlatformExercises,
  usePlatformEquipmentOptions,
  useExportPlatformExercisesCsv,
  useImportPlatformExercisesCsv,
  useHardDeletePlatformExercise,
  useBulkOperationPlatformExercises,
  usePlatformExerciseDetail,
  BulkOperation
} from './api';
import {
  PlatformExerciseSummaryResponse,
  getExerciseCategoryName,
  getDifficultyLevelName,
  ExerciseCategory,
  DifficultyLevel
} from './types';

export function PlatformExercisesPage() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ExerciseCategory | undefined>();
  const [selectedDifficulty, setSelectedDifficulty] = useState<DifficultyLevel | undefined>();
  const [selectedEquipment, setSelectedEquipment] = useState<number | undefined>();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState<PlatformExerciseSummaryResponse | undefined>();
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'name', direction: 'asc' });
  const [previewExerciseId, setPreviewExerciseId] = useState<number | null>(null);
  const [showInactiveWarning, setShowInactiveWarning] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const { addNotification } = useUIStore();
  const deleteMutation = useDeletePlatformExercise();
  const reactivateMutation = useReactivatePlatformExercise();
  const reorderMutation = useReorderPlatformExercises();
  const exportCsvMutation = useExportPlatformExercisesCsv();
  const importCsvMutation = useImportPlatformExercisesCsv();
  const hardDeleteMutation = useHardDeletePlatformExercise();
  const bulkOperationMutation = useBulkOperationPlatformExercises();
  const { data: equipmentData } = usePlatformEquipmentOptions();
  
  // Fetch detailed exercise data for preview
  const { data: previewExerciseData } = usePlatformExerciseDetail(previewExerciseId || 0);

  // Fetch exercise list with current filters
  const { data, isLoading, error, refetch } = usePlatformExerciseList({
    page: currentPage,
    pageSize: pageSize,
    search: searchTerm || undefined,
    category: selectedCategory,
    difficulty: selectedDifficulty,
    primaryEquipmentId: selectedEquipment,
    includeInactive,
    sortBy: sortConfig.field,
    sortDirection: sortConfig.direction,
  });

  // Bulk selection hook
  const {
    selectedItems,
    isAllSelected,
    isPartiallySelected,
    toggleSelection,
    selectAll,
    clearSelection,
    selectedIds
  } = useBulkSelection<PlatformExerciseSummaryResponse>({
    items: data?.exercises || [],
    keyExtractor: (exercise) => exercise.id,
  });

  const getSelectedIds = () => Array.from(selectedIds) as number[];

  const handleCreateNew = () => {
    setEditingExercise(undefined);
    setFormMode('create');
    setIsFormOpen(true);
  };

  // Handle URL parameters for quick actions
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'new') {
      handleCreateNew();
      // Clean up the URL parameter
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const handleEdit = (exercise: PlatformExerciseSummaryResponse) => {
    setEditingExercise(exercise);
    setFormMode('edit');
    setIsFormOpen(true);
  };

  const handleDelete = async (exercise: PlatformExerciseSummaryResponse) => {
    if (!window.confirm(`Are you sure you want to deactivate "${exercise.name}"?`)) {
      return;
    }

    try {
      await deleteMutation.mutateAsync({ id: exercise.id });
      addNotification({
        type: 'success',
        title: 'Exercise Deactivated',
        message: `${exercise.name} has been deactivated successfully.`,
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Deactivate Exercise',
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleReactivate = async (exercise: PlatformExerciseSummaryResponse) => {
    try {
      await reactivateMutation.mutateAsync({ id: exercise.id });
      addNotification({
        type: 'success',
        title: 'Exercise Reactivated',
        message: `${exercise.name} has been reactivated successfully.`,
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Reactivate Exercise',
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleHardDelete = async (exercise: PlatformExerciseSummaryResponse) => {
    const confirmed = window.confirm(
      `⚠️ PERMANENT DELETE WARNING ⚠️\n\nThis will PERMANENTLY DELETE "${exercise.name}" from the database.\n\nThis action CANNOT be undone!\n\nAre you absolutely sure you want to proceed?`
    );
    
    if (!confirmed) return;

    // Double confirmation for hard delete
    const doubleConfirmed = window.confirm(
      `FINAL CONFIRMATION\n\nYou are about to PERMANENTLY DELETE:\n"${exercise.name}"\n\nThis is your last chance to cancel.\n\nProceed with PERMANENT deletion?`
    );
    
    if (!doubleConfirmed) return;

    try {
      await hardDeleteMutation.mutateAsync({ id: exercise.id });
      addNotification({
        type: 'success',
        title: 'Exercise Permanently Deleted',
        message: `${exercise.name} has been permanently removed from the database.`,
      });
      clearSelection(); // Clear selection after delete
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Delete Exercise',
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleBulkOperation = async (operation: BulkOperation) => {
    const selectedIds = getSelectedIds();
    if (selectedIds.length === 0) return;

    const operationNames = {
      [BulkOperation.Deactivate]: 'deactivate',
      [BulkOperation.Reactivate]: 'reactivate', 
      [BulkOperation.HardDelete]: 'permanently delete'
    };

    const operationName = operationNames[operation];
    let confirmed = false;

    if (operation === BulkOperation.HardDelete) {
      // Special confirmation for hard delete
      confirmed = window.confirm(
        `⚠️ BULK PERMANENT DELETE WARNING ⚠️\n\nThis will PERMANENTLY DELETE ${selectedIds.length} exercises from the database.\n\nThis action CANNOT be undone!\n\nAre you absolutely sure you want to proceed?`
      );
      
      if (confirmed) {
        confirmed = window.confirm(
          `FINAL CONFIRMATION\n\nYou are about to PERMANENTLY DELETE ${selectedIds.length} exercises.\n\nThis is your last chance to cancel.\n\nProceed with BULK PERMANENT deletion?`
        );
      }
    } else {
      confirmed = window.confirm(
        `Are you sure you want to ${operationName} ${selectedIds.length} selected exercises?`
      );
    }

    if (!confirmed) return;

    try {
      const result = await bulkOperationMutation.mutateAsync({
        exerciseIds: selectedIds,
        operation: operation
      });

      addNotification({
        type: 'success',
        title: 'Bulk Operation Completed',
        message: result.message,
      });

      if (result.errors && result.errors.length > 0) {
        addNotification({
          type: 'warning',
          title: 'Some Operations Failed',
          message: `${result.errors.length} items had issues. Check console for details.`,
        });
        console.warn('Bulk operation errors:', result.errors);
      }

      clearSelection();
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Bulk Operation Failed',
        message: error.message || 'An unexpected error occurred.',
      });
    }
  };

  const handleReorder = async (reorderedExercises: PlatformExerciseSummaryResponse[]) => {
    const exerciseIds = reorderedExercises.map(exercise => exercise.id);
    
    try {
      await reorderMutation.mutateAsync({ exerciseIds });
      addNotification({
        type: 'success',
        title: 'Exercises Reordered',
        message: 'Exercise order has been updated successfully.',
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to Reorder Exercises',
        message: error.message || 'An unexpected error occurred.',
      });
      // Refetch to restore original order
      refetch();
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    refetch();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory(undefined);
    setSelectedDifficulty(undefined);
    setSelectedEquipment(undefined);
    setIncludeInactive(false);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleCsvExport = async () => {
    try {
      const blob = await exportCsvMutation.mutateAsync({
        includeInactive,
        search: searchTerm || undefined,
        category: selectedCategory,
        difficulty: selectedDifficulty,
        primaryEquipmentId: selectedEquipment,
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `platform_exercises_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: error.message || 'Failed to export exercise data.',
      });
    }
  };

  const handleCsvImport = async (file: File) => {
    const result = await importCsvMutation.mutateAsync({ file });
    return {
      created: result.summary.created,
      updated: result.summary.updated,
      skipped: result.summary.skipped,
      errors: result.errors,
    };
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      addNotification({
        type: 'error',
        title: 'Invalid File Type',
        message: 'Please select a CSV file.',
      });
      return;
    }

    try {
      const result = await handleCsvImport(file);
      
      addNotification({
        type: 'success',
        title: 'Import Successful',
        message: `Created: ${result.created}, Updated: ${result.updated}, Skipped: ${result.skipped}`,
      });

      if (result.errors.length > 0) {
        console.warn('Import warnings:', result.errors);
        addNotification({
          type: 'warning',
          title: 'Import Warnings',
          message: `${result.errors.length} items had issues. Check console for details.`,
        });
      }
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Import Failed',
        message: error.message || 'An unexpected error occurred during import.',
      });
    } finally {
      // Reset file input
      const input = document.getElementById('csv-import-input') as HTMLInputElement;
      if (input) {
        input.value = '';
      }
    }
  };

  const getDifficultyBadgeColor = (difficulty: DifficultyLevel) => {
    switch (difficulty) {
      case DifficultyLevel.Beginner:
        return 'bg-green-100 text-green-800';
      case DifficultyLevel.Intermediate:
        return 'bg-blue-100 text-blue-800';
      case DifficultyLevel.Advanced:
        return 'bg-orange-100 text-orange-800';
      case DifficultyLevel.Expert:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Define bulk actions
  const bulkActions: BulkAction[] = [
    {
      id: 'bulk-deactivate',
      label: 'Deactivate Selected',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636" /></svg>,
      variant: 'outline',
      onClick: () => handleBulkOperation(BulkOperation.Deactivate),
      disabled: bulkOperationMutation.isPending
    },
    {
      id: 'bulk-reactivate',
      label: 'Reactivate Selected',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
      variant: 'outline',
      onClick: () => handleBulkOperation(BulkOperation.Reactivate),
      disabled: bulkOperationMutation.isPending
    },
    {
      id: 'bulk-hard-delete',
      label: 'Permanently Delete',
      icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
      variant: 'destructive',
      onClick: () => handleBulkOperation(BulkOperation.HardDelete), 
      disabled: bulkOperationMutation.isPending
    }
  ];

  // Define table columns for the drag and drop table
  const columns: TableColumn<PlatformExerciseSummaryResponse>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (exercise) => (
        <div>
          <div className="font-medium text-gray-900">{exercise.name}</div>
          {exercise.description && (
            <div className="text-sm text-gray-500 mt-1">
              {exercise.description.length > 80
                ? `${exercise.description.substring(0, 80)}...`
                : exercise.description}
            </div>
          )}
          {exercise.tags && (
            <div className="text-xs text-blue-600 mt-1">
              {exercise.tags.split(',').slice(0, 3).map(tag => tag.trim()).join(', ')}
              {exercise.tags.split(',').length > 3 && '...'}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (exercise) => (
        <span className="text-gray-700">
          {getExerciseCategoryName(exercise.category)}
        </span>
      ),
    },
    {
      key: 'difficulty',
      label: 'Difficulty',
      sortable: true,
      render: (exercise) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDifficultyBadgeColor(exercise.difficulty)}`}>
          {getDifficultyLevelName(exercise.difficulty)}
        </span>
      ),
    },
    {
      key: 'primaryEquipment',
      label: 'Primary Equipment',
      sortable: true,
      render: (exercise) => (
        <span className="text-gray-700">
          {exercise.primaryEquipmentName}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (exercise) => (
        exercise.isActive ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Inactive
          </span>
        )
      ),
    },
  ];

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Platform Exercises</h1>
        </div>
        <ApiErrorFallback 
          error={error}
          onRetry={() => refetch()}
          title="Unable to load exercises"
          description="There was a problem loading the exercise list."
          showDetails={true}
        />
      </div>
    );
  }

  const equipmentOptions = equipmentData?.equipment || [];

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Exercises</h1>
          <p className="text-gray-600 text-sm">
            Manage global exercise library available to all studios
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1 mr-2 p-1 bg-gray-50 rounded-md">
            <Button
              onClick={handleCsvExport}
              variant="outline"
              size="sm"
              disabled={exportCsvMutation.isPending}
              className="flex items-center space-x-1 h-8"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-xs">{exportCsvMutation.isPending ? 'Exporting...' : 'Export'}</span>
            </Button>
            <Button
              onClick={() => document.getElementById('csv-import-input')?.click()}
              variant="outline"
              size="sm"
              disabled={importCsvMutation.isPending}
              className="flex items-center space-x-1 h-8"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              <span className="text-xs">{importCsvMutation.isPending ? 'Importing...' : 'Import'}</span>
            </Button>
          </div>
          <div className="flex items-center space-x-1 mr-2 p-1 bg-blue-50 rounded-md">
            <Button
              onClick={() => {
                const link = document.createElement('a');
                link.href = '/api/platform/exercises/backup';
                link.download = `exercises_backup_${new Date().toISOString().split('T')[0]}.csv`;
                link.click();
              }}
              variant="outline"
              size="sm"
              className="flex items-center space-x-1 h-8 text-blue-700 border-blue-200 hover:bg-blue-100"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="text-xs">Backup</span>
            </Button>
            <Button
              onClick={() => setShowInactiveWarning(!showInactiveWarning)}
              variant="outline"
              size="sm"
              className="flex items-center space-x-1 h-8 text-blue-700 border-blue-200 hover:bg-blue-100"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-xs">Audit</span>
            </Button>
          </div>
          <Button onClick={handleCreateNew} className="bg-green-600 hover:bg-green-700">
            ➕ Create New Exercise
          </Button>
        </div>
      </div>

      {/* Hidden file input for CSV import */}
      <input
        id="csv-import-input"
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Audit Trail Panel */}
      {showInactiveWarning && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2">📋 Audit Trail & System Health</h3>
                <p className="text-sm text-blue-700">
                  Recent activity and system status for platform exercise management
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInactiveWarning(false)}
                className="text-blue-700 border-blue-200 hover:bg-blue-100"
              >
                ✕ Close
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white p-3 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-green-600">{data?.pagination?.totalCount || 0}</div>
                <div className="text-sm text-gray-600">Total Exercises</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-blue-600">
                  {data?.exercises?.filter(e => e.isActive).length || 0}
                </div>
                <div className="text-sm text-gray-600">Active Exercises</div>
              </div>
              <div className="bg-white p-3 rounded-lg border border-blue-200">
                <div className="text-2xl font-bold text-orange-600">
                  {selectedItems.length}
                </div>
                <div className="text-sm text-gray-600">Currently Selected</div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-gray-900 mb-3">⚡ Quick Actions Available</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div className="flex items-center space-x-2 text-green-700">
                  <span>✅</span> <span>Bulk Operations</span>
                </div>
                <div className="flex items-center space-x-2 text-green-700">
                  <span>✅</span> <span>Hard Delete</span>
                </div>
                <div className="flex items-center space-x-2 text-green-700">
                  <span>✅</span> <span>CSV Import/Export</span>
                </div>
                <div className="flex items-center space-x-2 text-green-700">
                  <span>✅</span> <span>Backup System</span>
                </div>
                <div className="flex items-center space-x-2 text-green-700">
                  <span>✅</span> <span>Instructor Preview</span>
                </div>
                <div className="flex items-center space-x-2 text-green-700">
                  <span>✅</span> <span>Drag & Drop Reorder</span>
                </div>
                <div className="flex items-center space-x-2 text-green-700">
                  <span>✅</span> <span>Advanced Filtering</span>
                </div>
                <div className="flex items-center space-x-2 text-green-700">
                  <span>✅</span> <span>Multi-Select</span>
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-semibold text-yellow-800 mb-2">⚠️ SuperAdmin Safety Features</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Double confirmation required for permanent deletions</li>
                <li>• Comprehensive audit logging for all operations</li>
                <li>• Automatic backup creation before bulk operations</li>
                <li>• Preview mode to verify exercise appearance to instructors</li>
                <li>• Rollback capability through CSV import/export</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="py-3">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-2 items-center">
            <div className="flex-1 min-w-[200px]">
              <Input
                id="search"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search exercises..."
                className="h-8 text-sm"
              />
            </div>
            
            <select
              id="category"
              value={selectedCategory || ''}
              onChange={(e) => setSelectedCategory(e.target.value ? parseInt(e.target.value) as ExerciseCategory : undefined)}
              className="h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]"
            >
              <option value="">All Categories</option>
              {Object.values(ExerciseCategory)
                .filter(value => typeof value === 'number')
                .map((category) => (
                  <option key={category} value={category}>
                    {getExerciseCategoryName(category as ExerciseCategory)}
                  </option>
                ))}
            </select>

            <select
              id="difficulty"
              value={selectedDifficulty || ''}
              onChange={(e) => setSelectedDifficulty(e.target.value ? parseInt(e.target.value) as DifficultyLevel : undefined)}
              className="h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]"
            >
              <option value="">All Difficulties</option>
              {Object.values(DifficultyLevel)
                .filter(value => typeof value === 'number')
                .map((difficulty) => (
                  <option key={difficulty} value={difficulty}>
                    {getDifficultyLevelName(difficulty as DifficultyLevel)}
                  </option>
                ))}
            </select>

            <select
              id="equipment"
              value={selectedEquipment || ''}
              onChange={(e) => setSelectedEquipment(e.target.value ? parseInt(e.target.value) : undefined)}
              className="h-8 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[120px]"
            >
              <option value="">All Equipment</option>
              {equipmentOptions.map((equipment) => (
                <option key={equipment.id} value={equipment.id}>
                  {equipment.name}
                </option>
              ))}
            </select>

            <div className="flex items-center space-x-1">
              <input
                id="includeInactive"
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="includeInactive" className="text-xs text-gray-700 whitespace-nowrap">
                Inactive
              </label>
            </div>

            <div className="flex space-x-1">
              <Button type="submit" variant="outline" size="sm" className="h-8 px-3">
                Search
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-8 px-3" onClick={clearFilters}>
                Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>


      {/* Bulk Actions Toolbar */}
      <BulkActionsToolbar
        selectedCount={selectedItems.length}
        actions={bulkActions}
        onClearSelection={clearSelection}
      />

      {/* Exercises Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingState message="Loading exercises..." />
          ) : !data?.exercises.length ? (
            <EmptyState
              title="No exercises found"
              description={
                searchTerm || selectedCategory || selectedDifficulty || selectedEquipment || includeInactive
                  ? 'Try adjusting your filters or create new exercises.'
                  : 'Get started by creating your first exercise.'
              }
              actionLabel="Create New Exercise"
              onAction={handleCreateNew}
              showCard={false}
            />
          ) : (
            <>
              {/* Top Pagination - Compact */}
              {data.pagination && (
                <div className="flex justify-between items-center mb-4 py-2 border-b border-gray-100">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>
                      Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, data.pagination.totalCount)} of {data.pagination.totalCount}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                      className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={10}>10 per page</option>
                      <option value={15}>15 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1 || isLoading}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-2 py-1 text-sm">
                        {currentPage} of {data.pagination.totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(Math.min(data.pagination.totalPages, currentPage + 1))}
                        disabled={currentPage >= data.pagination.totalPages || isLoading}
                        className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <DragDropTable
                items={data.exercises}
                columns={columns}
                keyExtractor={(exercise) => exercise.id}
                onReorder={handleReorder}
                isLoading={reorderMutation.isPending}
                sortConfig={sortConfig}
                onSort={handleSort}
                enableBulkSelection={true}
                selectedIds={selectedIds}
                onSelectionChange={toggleSelection}
                onSelectAll={selectAll}
                onClearSelection={clearSelection}
                isAllSelected={isAllSelected}
                isPartiallySelected={isPartiallySelected}
                renderActions={(exercise) => (
                  <div className="flex items-center space-x-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPreviewExerciseId(exercise.id)}
                      className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                      title="Preview how this exercise appears to instructors"
                    >
                      📱 Preview
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(exercise)}
                    >
                      Edit
                    </Button>
                    {exercise.isActive ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(exercise)}
                        disabled={deleteMutation.isPending}
                      >
                        {deleteMutation.isPending ? 'Deactivating...' : 'Deactivate'}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleReactivate(exercise)}
                        disabled={reactivateMutation.isPending}
                      >
                        {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleHardDelete(exercise)}
                      disabled={hardDeleteMutation.isPending}
                      className="bg-red-600 hover:bg-red-700 text-white border-red-600"
                      title="Permanently delete this exercise (cannot be undone)"
                    >
                      {hardDeleteMutation.isPending ? 'Deleting...' : '🗑️ Delete'}
                    </Button>
                  </div>
                )}
              />

              {/* Enhanced Pagination */}
              {data.pagination && (
                <EnhancedPagination
                  pagination={data.pagination}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={handlePageSizeChange}
                  isLoading={isLoading}
                  className="mt-4"
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Exercise Form Modal */}
      <ExerciseForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        exercise={editingExercise}
        mode={formMode}
      />

      {/* Instructor Preview Modal */}
      <InstructorPreviewModal
        exercise={previewExerciseData || null}
        isOpen={!!previewExerciseId}
        onClose={() => setPreviewExerciseId(null)}
      />
    </div>
  );
}